import { Dialog, Transition } from '@headlessui/react';
import {
    BuildingStorefrontIcon,
    HomeIcon,
    UsersIcon,
    WrenchScrewdriverIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import clsx from 'clsx';
import { ChevronsRightIcon } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PlatoLogo } from '../assets/icons/plato-logo.tsx';
import { LoadingBlockFullScreen } from '../components/loading/loading-block-full-screen.tsx';
import { Button } from '../components/ui/button.tsx';
import { DialogProvider } from '../features/dialog/dialog-provider.tsx';
import { useSignOut } from '../lib/hooks/use-sign-out.ts';
import { api } from '../utils/api.ts';
import { getFullName } from '../utils/name.ts';

// MARK - clean this up
type IconType = typeof HomeIcon;

type NavigationItem = {
    name: string;
    href: string;
    icon: IconType;
    mustBeExact?: boolean;
};
const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, mustBeExact: true },
    { name: 'Customers', href: '/admin/customers', icon: BuildingStorefrontIcon },
    { name: 'Admin Users', href: '/admin/admin-users', icon: UsersIcon },
    { name: 'Tools', href: '/admin/tools', icon: WrenchScrewdriverIcon },
];

export const SuperAdminLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { signOutFn } = useSignOut();

    const {
        data: superAdmin,
        error,
        isLoading,
    } = api.superAdmin.me.getMe.useQuery(undefined, {
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (!error || !(error instanceof TRPCClientError)) {
            return;
        }
        const data: unknown = error.data;
        if (typeof data !== 'object' || data === null || !('code' in data)) {
            return;
        }
        if (data.code === 'FORBIDDEN') {
            void navigate('/', { replace: true });
        } else if (data.code === 'NOT_FOUND') {
            signOutFn();
        }
    }, [error, navigate, signOutFn]);

    useEffect(() => {
        if (!superAdmin || process.env.NODE_ENV === 'development') {
            return;
        }
        Sentry.setUser({
            id: superAdmin.id,
            email: superAdmin.email,
            name: getFullName(superAdmin.firstName, superAdmin.lastName),
            role: 'SUPER_ADMIN',
        });
        Sentry.setTag('admin', true);
    }, [superAdmin]);

    const isCurrent = (item: NavigationItem) => {
        const pathname = location.pathname + location.search + location.hash;
        if (!pathname) {
            return false;
        }
        const href = item.href;
        if (!href) {
            return false;
        }

        if (item.mustBeExact) {
            return href === pathname;
        } else {
            return pathname.startsWith(href);
        }
    };

    if (isLoading || !superAdmin) {
        return <LoadingBlockFullScreen />;
    }

    return (
        <DialogProvider>
            <div>
                <Transition.Root show={sidebarOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                        <Transition.Child
                            as={Fragment}
                            enter="transition-opacity ease-linear duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity ease-linear duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-neutral-900/80" />
                        </Transition.Child>

                        <div className="fixed inset-0 flex">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="-translate-x-full"
                                enterTo="translate-x-0"
                                leave="transition ease-in-out duration-300 transform"
                                leaveFrom="translate-x-0"
                                leaveTo="-translate-x-full"
                            >
                                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-in-out duration-300"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="ease-in-out duration-300"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                            <button
                                                type="button"
                                                className="-m-2.5 p-2.5"
                                                onClick={() => {
                                                    setSidebarOpen(false);
                                                }}
                                            >
                                                <span className="sr-only">Close sidebar</span>
                                                <XMarkIcon
                                                    className="h-6 w-6 text-white"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </Transition.Child>
                                    {/* Sidebar component, swap this element with another sidebar if you like */}
                                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-blue-600 px-6 pb-4">
                                        <div className="flex h-16 shrink-0 items-center">
                                            <PlatoLogo className="h-8" logoColor="white" />
                                        </div>
                                        <nav className="flex flex-1 flex-col">
                                            <ul className="flex flex-1 flex-col gap-y-7">
                                                <li>
                                                    <ul className="-mx-2 space-y-1">
                                                        {navigation.map((item) => (
                                                            <li key={item.name}>
                                                                <Link
                                                                    to={item.href}
                                                                    className={clsx(
                                                                        isCurrent(item)
                                                                            ? 'bg-blue-700 text-white'
                                                                            : 'text-indigo-200 hover:bg-blue-700 hover:text-white',
                                                                        'group flex gap-x-3 rounded-md p-2 text-base font-semibold leading-6',
                                                                    )}
                                                                >
                                                                    <item.icon
                                                                        className={clsx(
                                                                            isCurrent(item)
                                                                                ? 'text-white'
                                                                                : 'text-indigo-200 group-hover:text-white',
                                                                            'h-6 w-6 shrink-0',
                                                                        )}
                                                                        aria-hidden="true"
                                                                    />
                                                                    {item.name}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </Dialog>
                </Transition.Root>

                {/* Static sidebar for desktop */}
                <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-blue-600 px-6 pb-4">
                        <div className="flex h-16 shrink-0 items-center">
                            <PlatoLogo className="h-8" logoColor="white" />
                            <div className="ml-3 font-medium text-white">Plato Admin Portal</div>
                        </div>
                        <nav className="flex flex-1 flex-col">
                            <ul className="flex flex-1 flex-col gap-y-7">
                                <li>
                                    <ul className="-mx-2 space-y-1">
                                        {navigation.map((item) => (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    className={clsx(
                                                        isCurrent(item)
                                                            ? 'bg-blue-700 text-white'
                                                            : 'text-indigo-200 hover:bg-blue-700 hover:text-white',
                                                        'group flex gap-x-3 rounded-md p-2 text-base font-semibold leading-6',
                                                    )}
                                                >
                                                    <item.icon
                                                        className={clsx(
                                                            isCurrent(item)
                                                                ? 'text-white'
                                                                : 'text-indigo-200 group-hover:text-white',
                                                            'h-6 w-6 shrink-0',
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                    {item.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            </ul>
                        </nav>
                        <Button onClick={signOutFn}>Sign Out</Button>
                    </div>
                </div>
                <div className="flex lg:pl-72 h-dvh">
                    <div className="top-0 bottom-0 bg-blue-600 p-2 py-4 lg:hidden">
                        <Button
                            variant="outline"
                            size="icon-s"
                            icon={ChevronsRightIcon}
                            onClick={() => {
                                setSidebarOpen(true);
                            }}
                        />
                    </div>
                    <main className="w-full h-full overflow-auto max-h-dvh">
                        <Outlet />
                    </main>
                </div>
            </div>
        </DialogProvider>
    );
};
